import { DateTime } from 'luxon';

import PreSaleHeatmap from '@/common/models/presale-heatmap/PreSaleHeatmap';
import { toServerDateTime } from '@/globals';
import Validatable from '@/validator/Validatable';

interface IPreSaleHeatmapApiModelAPI {
  presaleheatmap_id: number;
  name: string;
  description: string;
  campaign_id: number;
  plan_id: number;
  created_on_utc: string;
  json_data_string: string;
  created_by: string;
  modified_on_utc: string;
  modified_by: string;
  uuid: string;
  public_plaintext_password: string;
}

interface IPreSaleHeatmapApiModel {
  id: number;
  name: string;
  description: string;
  campaignID: number;
  planID: number;
  createdOnUtc: DateTime;
  json: string;
  createdBy: string;
  modifiedOnUtc: DateTime;
  modifiedBy: string;
  uuid: string;
  publicPlaintextPassword: string;
}

export default class PreSaleHeatmapApiModel extends Validatable {
  name: string;
  description: string;
  json: string;
  campaignID: number;
  planID: number;
  createdOnUtc: DateTime;
  createdBy: string;
  modifiedOnUtc: DateTime;
  modifiedBy: string;
  uuid: string;
  publicPlaintextPassword: string;

  apiObject: IPreSaleHeatmapApiModelAPI;
  preSaleHeatmap: PreSaleHeatmap;

  constructor(
    id = -1,
    name?: string,
    description = '',
    json = null,
    campaignID = null,
    planID = null,
    createdOnUtc?: DateTime,
    createdBy?: string,
    modifiedOnUtc?: DateTime,
    modifiedBy?: string,
    uuid?: string,
    publicPlaintextPassword = '',
  ) {
    super();
    this.id = id;
    this.name = name;
    this.description = description;
    this.json = json;
    this.campaignID = campaignID;
    this.planID = planID;
    this.createdOnUtc = createdOnUtc;
    this.createdBy = createdBy;
    this.modifiedOnUtc = modifiedOnUtc;
    this.modifiedBy = modifiedBy;
    this.uuid = uuid;
    this.publicPlaintextPassword = publicPlaintextPassword;

    this.apiObject = null;
    this.preSaleHeatmap = null;
  }

  get originalObject() {
    if (this.apiObject !== null) {
      return PreSaleHeatmapApiModel.fromApi(this.apiObject);
    }
    return null;
  }

  get endpoint() {
    return `/pre-sale-heatmaps/${this.id}`;
  }

  get lastModifiedOnFormatted() {
    return this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_SHORT);
  }

  static fromObject(obj: IPreSaleHeatmapApiModel) {
    return super.fromObject(obj, new PreSaleHeatmapApiModel());
  }

  static fromApi(apiObject: IPreSaleHeatmapApiModelAPI) {
    return PreSaleHeatmapApiModel.fromObject({
      id: apiObject.presaleheatmap_id,
      name: apiObject.name,
      description: apiObject.description,
      campaignID: apiObject.campaign_id,
      planID: apiObject.plan_id,
      createdOnUtc:
        apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      json: apiObject.json_data_string,
      createdBy: apiObject.created_by,
      modifiedOnUtc:
        apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      modifiedBy: apiObject.modified_by,
      uuid: apiObject.uuid,
      publicPlaintextPassword: apiObject.public_plaintext_password,
    });
  }

  toApi(): IPreSaleHeatmapApiModelAPI {
    return {
      presaleheatmap_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      description: this.description,
      campaign_id: this.campaignID,
      plan_id: this.planID,
      json_data_string: this.json,
      created_on_utc: toServerDateTime(this.createdOnUtc),
      created_by: this.createdBy,
      modified_on_utc: toServerDateTime(this.modifiedOnUtc),
      modified_by: this.modifiedBy,
      uuid: this.uuid,
      public_plaintext_password: this.publicPlaintextPassword,
    };
  }

  static validatorCreateRules() {
    return {
      name: 'required',
    };
  }
}
